import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { roleLabel, canCreate } from "@/lib/c";
import { Icon } from "@/components/kit";

const NAV = [
  ["/", "Beranda", "home-variant"], ["/kios", "Kios Sampah", "storefront-outline"], ["/bsi", "Bank Sampah Induk", "piggy-bank-outline"],
  ["/pdu", "PDU", "recycle"], ["/kompos", "Rumah Kompos", "sprout-outline"], ["/tpa", "TPA Iwaka", "dump-truck"],
  ["/riwayat", "Riwayat Laporan", "file-document-multiple-outline"], ["/galeri", "Galeri Dokumentasi", "image-multiple-outline"],
  ["/verifikasi", "Verifikasi", "clipboard-check-outline", "badge"], ["/recap", "Rekap & Uraian", "file-chart-outline"],
  ["/news", "NEWS PAPA BERSERI", "newspaper-variant-outline"], ["/video", "Video Beranda", "movie-open-play-outline", "admin"],
  ["/jenis-sampah", "Jenis Sampah", "format-list-bulleted-type", "admin"], ["/fasilitas", "Data Fasilitas", "map-marker-multiple-outline"],
  ["/tim", "Tim Efektif", "account-group-outline"], ["/tutorial", "Video Tutorial", "school-outline"], ["/bagikan-tim", "Bagikan ke Tim Efektif", "share-variant-outline", "admin"],
  ["/akun", "Kelola Akun", "account-cog-outline", "admin"], ["/profil", "Profil", "account-circle-outline"],
];
const TABS = [["/", "Beranda", "home-variant"], ["/kios", "Kios", "storefront-outline"], ["/bsi", "Bank Induk", "piggy-bank-outline"],
  ["/pdu", "PDU", "recycle"], ["/kompos", "Kompos", "sprout-outline"], ["/tpa", "TPA", "dump-truck"]];

export const useTasks = () => {
  const { user } = useAuth();
  return useQuery({ queryKey: ["dashboard", "tasks"], queryFn: () => api("/tasks"), enabled: !!user });
};
const active = (path, href) => (href === "/" ? path === "/" : path.startsWith(href));

function Sidebar() {
  const { user } = useAuth();
  const path = useLocation().pathname;
  const count = useTasks().data?.count ?? 0;
  return (
    <aside className="hidden lg:flex w-[264px] shrink-0 bg-[var(--brand)] text-white flex-col px-3.5 pt-5 h-screen sticky top-0" data-testid="sidebar">
      <div className="flex items-center gap-2.5 pb-4 border-b border-white/15">
        <div className="w-[52px] h-[60px] rounded-[14px] bg-white flex items-center justify-center"><img src="/assets/images/logo-mimika.png" alt="Logo Mimika" className="w-10 h-[50px] object-contain" /></div>
        <div><p className="text-2xl font-black leading-6">SIAP</p><p className="text-[15px] font-black">PAPA BERSERI</p><p className="text-xs opacity-75">Kabupaten Mimika</p></div>
      </div>
      <nav className="flex-1 overflow-y-auto py-2 space-y-1">
        {NAV.filter((n) => n[3] !== "admin" || user?.role === "admin").map(([href, label, icon, f]) => (
          <Link key={href} to={href} data-testid={`side-nav-${href === "/" ? "beranda" : href.slice(1)}`}
            className={`flex items-center gap-3 h-12 px-3 rounded-[14px] font-bold transition-colors hover:bg-[var(--primary)] ${active(path, href) ? "bg-[var(--primary)]" : ""}`}>
            <Icon name={icon} /><span className="flex-1 truncate">{label}</span>
            {f === "badge" && count > 0 && <span className="min-w-6 h-6 px-1.5 rounded-full bg-[var(--warning)] text-[var(--on)] text-[13px] font-black flex items-center justify-center">{count}</span>}
          </Link>
        ))}
      </nav>
      <div className="py-4 border-t border-white/15">
        <p className="font-extrabold truncate">{user?.name}</p><p className="text-[13px] opacity-80 truncate">{roleLabel(user)}</p>
        <p className="text-[11px] opacity-60 font-extrabold mt-2">DINAS LINGKUNGAN HIDUP<br />KABUPATEN MIMIKA</p>
      </div>
    </aside>
  );
}

function BottomBar() {
  const path = useLocation().pathname;
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-[500] bg-white border-t border-[var(--border)] grid grid-cols-6 pb-[env(safe-area-inset-bottom)]" data-testid="bottom-bar">
      {TABS.map(([href, label, icon]) => (
        <Link key={href} to={href} data-testid={`tab-${href === "/" ? "beranda" : href.slice(1)}`} className="flex flex-col items-center py-2 gap-0.5">
          <span className={`px-3 py-1 rounded-full ${active(path, href) ? "bg-[var(--brand3)] text-[var(--brand)]" : "text-[var(--muted)]"}`}><Icon name={icon} size={24} /></span>
          <span className={`text-[11px] font-extrabold ${active(path, href) ? "text-[var(--brand)]" : "text-[var(--muted)]"}`}>{label}</span>
        </Link>
      ))}
    </nav>
  );
}

export function AppHeader({ title }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const count = useTasks().data?.count ?? 0;
  const [q, setQ] = useState("");
  const initials = (user?.name || "P").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const round = "w-11 h-11 rounded-full flex items-center justify-center relative";
  const bell = (
    <button className={`${round} bg-[var(--tert)]`} onClick={() => nav("/verifikasi")} data-testid="header-bell-button"><Icon name="bell-outline" size={24} />
      {count > 0 && <span className="absolute top-1 right-0.5 min-w-5 h-5 px-1 rounded-full bg-[var(--error)] text-white text-[11px] font-black flex items-center justify-center" data-testid="header-bell-badge">{count > 99 ? "99+" : count}</span>}
    </button>
  );
  const avatar = (
    <button className={`${round} bg-[var(--brand3)] overflow-hidden font-black text-[var(--brand)]`} onClick={() => nav("/profil")} data-testid="header-avatar-button">
      {user?.picture ? <img src={user.picture} alt="" className="w-11 h-11" /> : initials}
    </button>
  );
  return (
    <header className="sticky top-0 z-[400] bg-white/95 backdrop-blur border-b border-[var(--border)]">
      <div className="hidden lg:flex items-center gap-4 px-6 py-3">
        <h1 className="text-2xl font-black min-w-[160px] truncate">{title}</h1>
        <form className="flex-1 flex items-center gap-2 h-12 rounded-full bg-[var(--tert)] px-4" onSubmit={(e) => { e.preventDefault(); nav(`/riwayat?q=${encodeURIComponent(q)}`); }}>
          <Icon name="magnify" color="var(--muted)" /><input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari laporan, petugas, lokasi…" className="flex-1 bg-transparent outline-none" />
        </form>
        {bell}<div className="text-right"><p className="font-extrabold max-w-[220px] truncate">{user?.name}</p><p className="text-[13px] text-[var(--muted)] font-semibold">{roleLabel(user)}</p></div>{avatar}
      </div>
      <div className="lg:hidden flex items-center gap-2 px-4 py-2">
        <img src="/assets/images/logo-mimika.png" alt="" className="w-10 h-[50px] object-contain" data-testid="header-logo-mimika" />
        <div className="flex-1 min-w-0"><p className="text-[19px] font-black text-[var(--brand)] truncate">SIAP PAPA BERSERI</p><p className="text-sm font-bold text-[var(--muted)] truncate">{title === "Beranda" ? "DLH Kab. Mimika" : title}</p></div>
        <button className={`${round} bg-[var(--tert)]`} onClick={() => nav("/riwayat")} data-testid="header-history-button"><Icon name="history" size={24} /></button>{bell}{avatar}
      </div>
    </header>
  );
}

export function SubHeader({ title, sub, right }) {
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-[400] bg-white/95 backdrop-blur border-b border-[var(--border)] flex items-center gap-2 px-4 py-2.5">
      <button className="w-11 h-11 rounded-full bg-[var(--tert)] flex items-center justify-center" onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))} data-testid="back-button"><Icon name="arrow-left" size={24} /></button>
      <div className="flex-1 min-w-0 text-center"><h1 className="text-lg font-black truncate">{title}</h1>{sub && <p className="text-xs text-[var(--muted)] font-semibold truncate">{sub}</p>}</div>
      <div className="min-w-11 flex justify-end">{right}</div>
    </header>
  );
}

export function Shell({ children }) {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 pb-24 lg:pb-0">{children}</main>
      <BottomBar />
    </div>
  );
}

export function Fab({ category }) {
  const { user } = useAuth();
  const nav = useNavigate();
  if (!canCreate(user, category)) return null;
  const cat = category || (user?.facilities?.length === 1 ? user.facilities[0] : "");
  return (
    <button data-testid="fab-new-report" onClick={() => nav(`/report/new${cat ? `?category=${cat}` : ""}`)}
      className="fixed right-4 bottom-24 lg:bottom-6 z-[450] h-14 px-5 rounded-full bg-[var(--primary)] text-white font-extrabold text-[17px] flex items-center gap-2 shadow-[0_8px_16px_rgba(39,89,50,.3)] active:scale-95 transition-transform">
      <Icon name="file-document-edit-outline" />Buat Laporan
    </button>
  );
}
